// threadNoticeCopy — the words and look of a notice in the thread (pen "Chat –
// every entry" §8), per cause. Pure: Lingui descriptors, localized by the
// notice. Exhaustive over `SystemEntryKind`, so a new cause cannot ship
// without its words.
//
// Looks: `trail` is one quiet line (you chose this, nothing is wrong);
// `danger` / `warning` / `quiet` are a tinted box with plain words, never
// codes. The composer's own live notice keeps `interruptionCopy`.

import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { toDisplay } from "../../../app/externalError.js";
import type { IconName } from "../../Icon/Icon.js";
import type { InterruptionAction, SystemEntryKind } from "../types.js";

type NoticeLook = "trail" | "danger" | "warning" | "quiet";

export type NoticeCopy = {
    readonly look: NoticeLook;
    readonly icon: IconName;
    readonly title: MessageDescriptor;
    readonly body?: MessageDescriptor;
    readonly action?: NoticeAction;
};

type NoticeAction = {
    readonly type: InterruptionAction;
    readonly label: MessageDescriptor;
};

// What the notice knows about its place in the thread: whether the turn it
// closes made something (then "Nothing was changed" would be untrue), and
// whether it is the live tail (only then is the connection still being
// retried).
export type NoticeContext = {
    readonly turnMadeSomething: boolean;
    readonly live: boolean;
};

const continueAction: NoticeAction = {
    type: "continue",
    label: msg`Continue`,
};
const tryAgain: NoticeAction = { type: "try_again", label: msg`Try again` };

const savedSoFar = msg`Everything finished before this is saved.`;

const couldNotFinish = (context: NoticeContext): NoticeCopy => ({
    look: "danger",
    icon: "alert",
    title: msg`The assistant could not finish`,
    body: context.turnMadeSomething ? savedSoFar : msg`Nothing was changed.`,
    action: tryAgain,
});

export const threadNoticeCopy = (
    kind: SystemEntryKind,
    context: NoticeContext,
): NoticeCopy => {
    switch (kind.type) {
        case "interrupted":
            return {
                look: "trail",
                icon: "stop",
                title: msg`Stopped`,
                action: continueAction,
            };
        case "budget_halted":
            return {
                look: "trail",
                icon: "stop",
                title: msg`Paused to keep costs down`,
                action: continueAction,
            };
        case "failed": {
            if (kind.error === undefined) return couldNotFinish(context);
            const d = toDisplay(kind.error);
            return {
                look: "danger",
                icon: "alert",
                title: d.title,
                ...(d.body === undefined ? {} : { body: d.body }),
                ...(d.retriable ? { action: tryAgain } : {}),
            };
        }
        case "context_window_exceeded":
            return {
                look: "warning",
                icon: "conversation-long",
                title: msg`This conversation is getting long`,
                body: msg`Start a new one – the assistant brings a summary.`,
                action: {
                    type: "new_conversation",
                    label: msg`New conversation`,
                },
            };
        case "budget_used_up":
            return {
                look: "warning",
                icon: "budget",
                title: msg`The AI budget for this month is used up`,
                body: msg`Your administrator can raise it. Your work is saved.`,
            };
        case "connection_lost":
            return {
                look: "quiet",
                icon: "wifi-off",
                title: context.live
                    ? msg`Connection lost – trying again …`
                    : msg`The connection was lost`,
                action: {
                    type: kind.messageSaved ? "continue" : "try_again",
                    label: msg`Retry now`,
                },
            };
        case "unfinished":
            return {
                look: "warning",
                icon: "alert",
                title: msg`The work stopped before it was done`,
                body: savedSoFar,
                action: continueAction,
            };
        case "colleague_clash":
            return {
                look: "warning",
                icon: "alert",
                title: msg`A colleague changed this at the same time`,
                body: msg`Nothing was overwritten.`,
            };
        case "signed_out":
            return {
                look: "danger",
                icon: "alert",
                title: msg`You have been signed out`,
                body: msg`Everything before your last message is saved. Your last message was not sent.`,
                action: { type: "sign_in", label: msg`Sign in again` },
            };
        // A failure the app cannot name reads like any other: plain words,
        // no codes.
        case "unknown":
            return couldNotFinish(context);
        default:
            return exhaustSwitchCase(kind);
    }
};
